import { useState, useEffect } from "react";
import {
  DollarSign,
  TrendingUp,
  Calendar,
  Users,
  AlertCircle,
  CheckCircle,
  Clock,
  Receipt,
  BarChart3,
  FileText,
  LineChart,
} from "lucide-react";
import medicalDB from "../services/database";
import RevenueChart from "./RevenueChart";
import OutstandingPayments from "./OutstandingPayments";

const FinancialDashboard = () => {
  const [financialData, setFinancialData] = useState({
    daily: null,
    weekly: null,
    monthly: null,
    yearly: null,
  });
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [outstandingPayments, setOutstandingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState("daily");
  const [chartPeriod, setChartPeriod] = useState("daily");
  const [chartType, setChartType] = useState("line");

  useEffect(() => {
    loadFinancialData();
  }, []);

  const loadFinancialData = async () => {
    try {
      setLoading(true);

      const [daily, weekly, monthly, yearly, transactions] = await Promise.all([
        medicalDB.getDailyRevenue(),
        medicalDB.getWeeklyRevenue(),
        medicalDB.getMonthlyRevenue(),
        medicalDB.getYearlyRevenue(),
        medicalDB.getAllTransactions({ limit: 10 }),
      ]);

      setFinancialData({ daily, weekly, monthly, yearly });
      setRecentTransactions(transactions.slice(0, 5));

      // Get outstanding payments
      const outstanding = transactions.filter(
        (t) =>
          t.status === (medicalDB.PAYMENT_STATUS?.PENDING || "pending") ||
          t.status === (medicalDB.PAYMENT_STATUS?.PARTIAL || "partial")
      );
      setOutstandingPayments(outstanding.slice(0, 5));
    } catch (error) {
      console.error("Error loading financial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentData = () => {
    return financialData[selectedPeriod];
  };

  const getPeriodLabel = () => {
    switch (selectedPeriod) {
      case "daily":
        return "Today";
      case "weekly":
        return "This Week";
      case "monthly":
        return "This Month";
      case "yearly":
        return "This Year";
      default:
        return "Today";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case medicalDB.PAYMENT_STATUS?.PAID || "paid":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case medicalDB.PAYMENT_STATUS?.PARTIAL || "partial":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case medicalDB.PAYMENT_STATUS?.PENDING || "pending":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case medicalDB.PAYMENT_STATUS?.PAID || "paid":
        return "text-green-600 bg-green-50";
      case medicalDB.PAYMENT_STATUS?.PARTIAL || "partial":
        return "text-yellow-600 bg-yellow-50";
      case medicalDB.PAYMENT_STATUS?.PENDING || "pending":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64 bg-gray-200 rounded-lg"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  const currentData = getCurrentData();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">
            Financial Dashboard
          </h1>
        </div>

        {/* Period Selector */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          {["daily", "weekly", "monthly", "yearly"].map((period) => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedPeriod === period
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {period.charAt(0).toUpperCase() + period.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      {currentData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Total Revenue */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Revenue
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  ₪{currentData.totalRevenue.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500">{getPeriodLabel()}</p>
              </div>
            </div>
          </div>

          {/* Total Paid */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Paid</p>
                <p className="text-2xl font-bold text-gray-900">
                  ₪{currentData.totalPaid.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500">
                  {(
                    (currentData.totalPaid / currentData.totalRevenue) * 100 ||
                    0
                  ).toFixed(1)}
                  % of revenue
                </p>
              </div>
            </div>
          </div>

          {/* Outstanding Balance */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Outstanding</p>
                <p className="text-2xl font-bold text-gray-900">
                  ₪{currentData.outstandingBalance.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500">
                  {currentData.statusCounts.pending +
                    currentData.statusCounts.partial}{" "}
                  transactions
                </p>
              </div>
            </div>
          </div>

          {/* Transaction Count */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Receipt className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Transactions
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {currentData.transactionCount}
                </p>
                <p className="text-xs text-gray-500">
                  Avg: ₪{currentData.averageTransactionValue.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Transactions & Outstanding Payments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Transactions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Receipt className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                Recent Transactions
              </h3>
            </div>
          </div>
          <div className="p-6">
            {recentTransactions.length > 0 ? (
              <div className="space-y-4">
                {recentTransactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(transaction.status)}
                      <div>
                        <div className="font-medium text-gray-900">
                          {transaction.patient?.name || "Unknown Patient"}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <span>{transaction.receiptNumber}</span>
                          <span>•</span>
                          <Calendar className="h-3 w-3" />
                          {new Date(transaction.date).toLocaleDateString(
                            "en-IL"
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-gray-900">
                        ₪{transaction.totalAmount.toFixed(2)}
                      </div>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                          transaction.status
                        )}`}
                      >
                        {transaction.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Receipt className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p>No transactions yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Outstanding Payments */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                Outstanding Payments
              </h3>
            </div>
          </div>
          <div className="p-6">
            {outstandingPayments.length > 0 ? (
              <div className="space-y-4">
                {outstandingPayments.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between p-3 bg-red-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(transaction.status)}
                      <div>
                        <div className="font-medium text-gray-900">
                          {transaction.patient?.name || "Unknown Patient"}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <span>{transaction.receiptNumber}</span>
                          <span>•</span>
                          <Calendar className="h-3 w-3" />
                          {new Date(transaction.date).toLocaleDateString(
                            "en-IL"
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-red-600">
                        ₪
                        {(
                          transaction.totalAmount - transaction.paidAmount
                        ).toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">
                        of ₪{transaction.totalAmount.toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-400" />
                <p>All payments up to date!</p>
                <p className="text-sm">No outstanding balances</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Revenue Analytics
          </h3>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">
                Period:
              </label>
              <select
                value={chartPeriod}
                onChange={(e) => setChartPeriod(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setChartType("line")}
                className={`p-2 rounded-lg transition-colors ${
                  chartType === "line"
                    ? "bg-blue-100 text-blue-600"
                    : "text-gray-400 hover:text-gray-600"
                }`}
                title="Line Chart"
              >
                <LineChart className="h-4 w-4" />
              </button>
              <button
                onClick={() => setChartType("bar")}
                className={`p-2 rounded-lg transition-colors ${
                  chartType === "bar"
                    ? "bg-blue-100 text-blue-600"
                    : "text-gray-400 hover:text-gray-600"
                }`}
                title="Bar Chart"
              >
                <BarChart3 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
        <RevenueChart period={chartPeriod} chartType={chartType} />
      </div>

      {/* Outstanding Payments */}
      <OutstandingPayments limit={5} showHeader={true} />

      {/* Payment Status Overview */}
      {currentData && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Payment Status Overview - {getPeriodLabel()}
            </h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {currentData.statusCounts.paid || 0}
              </div>
              <div className="text-sm text-green-600 font-medium">Paid</div>
            </div>

            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {currentData.statusCounts.partial || 0}
              </div>
              <div className="text-sm text-yellow-600 font-medium">Partial</div>
            </div>

            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {currentData.statusCounts.pending || 0}
              </div>
              <div className="text-sm text-red-600 font-medium">Pending</div>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-600">
                {currentData.transactionCount}
              </div>
              <div className="text-sm text-gray-600 font-medium">Total</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialDashboard;
